<div class="main-content">
  <div class="container-fluid">
    <div class="admin-page-header">
      <div>
        <h3>Application — {{ serviceLabel }}</h3>
        <p class="text-muted">Fields marked <span class="req">*</span> are required.</p>
      </div>
    </div>

    <div *ngIf="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <form-error-summary [errors]="fieldErrors" (jumpTo)="jumpToField($event)"></form-error-summary>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- ── Q1. TELL US ABOUT YOUR PROJECT ──────────────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q1. Tell us about your project</h4>
        <p class="text-muted">Please provide the following information about your project.</p>

        <!-- Funding type -->
        <div class="form-group" id="field-funding_type">
          <label>Is your project funded through an EU programme, or through a national or regional programme? <span class="req">*</span></label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="funding_type" value="eu"> EU-funded project
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="funding_type" value="national"> Nationally or regionally funded project
            </label>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <div class="form-group" id="field-project_acronym">
              <label>The official short name of your project <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('project_acronym')?.invalid"
                     formControlName="project_acronym">
              <div *ngIf="formSubmitted && form.get('project_acronym')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div class="form-group" id="field-full_project_name">
              <label>The full title of your project, as it appears in your funding contract or grant agreement <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('full_project_name')?.invalid"
                     formControlName="full_project_name">
              <div *ngIf="formSubmitted && form.get('full_project_name')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
        </div>

        <!-- EU-funded conditional -->
        <div *ngIf="isEuFunded" class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-funding_programme">
              <label>Which EU programme funds your project? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="showEuFundingError()"
                      formControlName="funding_programme">
                <option value="">— Select —</option>
                <option *ngFor="let p of EU_FUNDING_PROGRAMMES" [value]="p">{{ p }}</option>
              </select>
              <div *ngIf="showEuFundingError()" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6" *ngIf="isOtherFundingProgramme">
            <div class="form-group">
              <label>Please specify the funding programme</label>
              <input type="text" class="form-control" formControlName="other_funding_programme">
            </div>
          </div>
        </div>

        <!-- Nationally funded conditional -->
        <div *ngIf="!isEuFunded" class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-national_programme_name">
              <label>Please name your national or regional funding programme <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="showNationalNameError()"
                     formControlName="national_programme_name">
              <div *ngIf="showNationalNameError()" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-national_programme_country">
              <label>Select the country under which it operates <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="showNationalCountryError()"
                      formControlName="national_programme_country">
                <option value="">— Select —</option>
                <option *ngFor="let c of COUNTRIES" [value]="c">{{ c }}</option>
              </select>
              <div *ngIf="showNationalCountryError()" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-grant_agreement">
              <label>
                Your project's Grant Agreement number (EU-funded) or national / regional grant, contract or project reference number <span class="req">*</span>
              </label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('grant_agreement')?.invalid"
                     formControlName="grant_agreement"
                     placeholder="e.g. 101000000">
              <div *ngIf="formSubmitted && form.get('grant_agreement')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-project_call_topic_id">
              <label>
                The code of the call under which your project was funded <span class="req">*</span>
              </label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('project_call_topic_id')?.invalid"
                     formControlName="project_call_topic_id"
                     placeholder="e.g. HORIZON-CL4-2023-TWIN-TRANSITION-01">
              <div *ngIf="formSubmitted && form.get('project_call_topic_id')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-coordinator_country">
              <label>In which country is your project's coordinating organisation based? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('coordinator_country')?.invalid"
                      formControlName="coordinator_country">
                <option value="">— Select —</option>
                <option *ngFor="let c of COUNTRIES" [value]="c">{{ c }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('coordinator_country')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-project_website">
              <label>Your project's official website <span class="req">*</span></label>
              <input type="url" class="form-control"
                     [class.input-error]="formSubmitted && form.get('project_website')?.invalid"
                     formControlName="project_website"
                     placeholder="https://www.yourproject.eu">
              <div *ngIf="formSubmitted && form.get('project_website')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-project_officer_name">
              <label>The name of the officer responsible for your project at the funding body <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('project_officer_name')?.invalid"
                     formControlName="project_officer_name">
              <div *ngIf="formSubmitted && form.get('project_officer_name')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>The e-mail of the officer responsible for your project at the funding body</label>
              <input type="email" class="form-control"
                     formControlName="project_officer_email"
                     placeholder="officer@ec.europa.eu">
            </div>
          </div>
        </div>
      </div>

      <!-- ── Q2. SELECT YOUR OPEN CALL TOPIC(S) ─────────────────────────────── -->
      <div class="form-section" id="field-topics">
        <h4 class="form-section-title">Q2. Select your open call topic(s) <span class="req">*</span></h4>
        <p class="text-muted">Please select between one and three open call topics for which you are requesting standardisation support. The selected topics should reflect your project's research and innovation outputs and the areas where standardisation support would add most value.</p>

        <div class="topics-picker" [class.topics-picker--error]="formSubmitted && selectedTopics.length === 0">

          <div class="topics-selected">
            <span class="topics-count" [class.topics-count--full]="selectedTopics.length === MAX_TOPICS">
              {{ selectedTopics.length }}/{{ MAX_TOPICS }} selected
            </span>
            <ng-container *ngIf="selectedTopics.length > 0; else noTopics">
              <span *ngFor="let t of selectedTopics" class="topic-tag">
                <span class="topic-tag-group">{{ t.groupLabel }}</span>
                {{ t.label }}
                <span class="topic-tag-remove" (click)="removeTopic(t.value)" title="Remove">&#x2715;</span>
              </span>
            </ng-container>
            <ng-template #noTopics>
              <span class="topics-empty">No topics selected yet — expand a category below.</span>
            </ng-template>
          </div>

          <div class="topics-tree">
            <div *ngFor="let group of TOPIC_GROUPS" class="tg">
              <div class="tg-header" (click)="toggleGroup(group.label)">
                <i class="fa fa-fw" [ngClass]="isGroupExpanded(group.label) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                <strong>{{ group.label }}</strong>
              </div>
              <div *ngIf="isGroupExpanded(group.label)" class="tg-body">
                <div *ngFor="let sub of group.subGroups" class="tg-sub">
                  <div class="tg-sub-header">
                    <span class="tg-expand" (click)="toggleSubGroup(sub.value)">
                      <i class="fa fa-fw" [ngClass]="isSubGroupExpanded(sub.value) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </span>
                    <span class="tg-item"
                          [class.tg-item--selected]="isTopicSelected(sub.value)"
                          [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(sub.value)"
                          (click)="selectTopic(sub.value, sub.label, group.label)">
                      {{ sub.label }}
                    </span>
                  </div>
                  <div *ngIf="isSubGroupExpanded(sub.value)" class="tg-children">
                    <div *ngFor="let child of sub.children" class="tg-child">
                      <span class="tg-item tg-item--child"
                            [class.tg-item--selected]="isTopicSelected(child.value)"
                            [class.tg-item--disabled]="!canSelectMore() && !isTopicSelected(child.value)"
                            (click)="selectTopic(child.value, child.label, group.label)">
                        {{ child.label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div *ngIf="formSubmitted && selectedTopics.length === 0" class="field-error">
          Please select at least one topic.
        </div>
      </div>

      <!-- ── Q3. EXPLAIN THE SUPPORT YOU ARE REQUESTING ─────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q3. Explain the support you are requesting</h4>
        <p class="text-muted">Please select the type(s) of standardisation support you are requesting, and briefly describe your project and your reasons for applying.</p>

        <div class="form-group" id="field-information_to_attain">
          <label>What kind of standardisation support are you looking for? <span class="req">*</span></label>
          <p class="text-muted small">Select all that apply.</p>
          <div class="checkbox-grid" [class.checkbox-grid--error]="formSubmitted && selectedInformation.length === 0">
            <div *ngFor="let opt of SUPPORT_OPTIONS" class="checkbox-option"
                 [class.checkbox-option--checked]="isInformationSelected(opt)">
              <label>
                <input type="checkbox"
                       [checked]="isInformationSelected(opt)"
                       (change)="toggleInformation(opt)">
                {{ opt }}
              </label>
            </div>
          </div>
          <div *ngIf="formSubmitted && selectedInformation.length === 0" class="field-error">
            Please select at least one option.
          </div>
        </div>

        <div *ngIf="isOtherSupportSelected" class="form-group">
          <label>Please specify the support you are looking for</label>
          <input type="text" class="form-control" formControlName="other_support_specification"
                 placeholder="Describe the support you need…">
        </div>

        <div class="form-group" id="field-project_short_description">
          <label>Briefly describe your project's objectives, context and expected results <span class="req">*</span></label>
          <textarea class="form-control" rows="5"
                    [class.input-error]="formSubmitted && form.get('project_short_description')?.invalid"
                    formControlName="project_short_description"
                    placeholder="Briefly describe your project (objectives, context, expected outcomes)…"></textarea>
          <div *ngIf="formSubmitted && form.get('project_short_description')?.invalid" class="field-error">
            This field is required.
          </div>
        </div>

        <div class="form-group" id="field-why_the_project_applying">
          <label>Explain why standardisation matters for your project and what you expect from HSbooster.eu II support <span class="req">*</span></label>
          <textarea class="form-control" rows="5"
                    [class.input-error]="formSubmitted && form.get('why_the_project_applying')?.invalid"
                    formControlName="why_the_project_applying"
                    placeholder="Explain how standardisation is relevant to your project and what you expect to achieve…"></textarea>
          <div *ngIf="formSubmitted && form.get('why_the_project_applying')?.invalid" class="field-error">
            This field is required.
          </div>
        </div>
      </div>

      <!-- ── Q4. WHEN DO YOU NEED THIS SUPPORT? ─────────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q4. When do you need this support?</h4>
        <p class="text-muted">Please indicate when you would like this support to start. This information is used to match your request against the availability that HSbooster.eu II experts have declared.</p>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-support_start">
              <label>When would you like this support to start? <span class="req">*</span></label>
              <select class="form-control"
                      [class.input-error]="formSubmitted && form.get('support_start')?.invalid"
                      formControlName="support_start">
                <option value="">— Select —</option>
                <option *ngFor="let opt of SUPPORT_START_OPTIONS" [value]="opt">{{ opt }}</option>
              </select>
              <div *ngIf="formSubmitted && form.get('support_start')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Is this request linked to a deadline or milestone? If so, specify</label>
              <input type="text" class="form-control"
                     formControlName="support_milestone"
                     placeholder="e.g. Project review in October 2026">
            </div>
          </div>
        </div>

        <div class="form-group" id="field-cluster_support">
          <label>Is this support needed for your project only or for a cluster of similar projects? <span class="req">*</span></label>
          <div>
            <label class="radio-block">
              <input type="radio" formControlName="cluster_support" value="project_only"> Only for my project
            </label>
            <label class="radio-block">
              <input type="radio" formControlName="cluster_support" value="cluster_only"> For a cluster of projects I have already identified
            </label>
            <label class="radio-block">
              <input type="radio" formControlName="cluster_support" value="both"> Both for my own project and for the cluster
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('cluster_support')?.invalid" class="field-error">
            Please select an option.
          </div>
        </div>

        <div *ngIf="clusterSelected" class="form-group">
          <label>Please share more details on the cluster projects and what needs the cluster collectively has</label>
          <textarea class="form-control" rows="5"
                    formControlName="cluster_details"
                    placeholder="Describe the cluster projects and their collective standardisation needs…"></textarea>
        </div>

      </div>

      <!-- ── Q5. DO YOU ALREADY KNOW OF RELEVANT STANDARDS? ─────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q5. Do you already know of relevant standards or SDOs you'd like to engage with?</h4>
        <p class="text-muted">If your project has already identified standards of interest, please provide details below.</p>

        <div class="form-group">
          <label>Does your project already know of relevant standards? <span class="req">*</span></label>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="know_standards" value="no"> No
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="know_standards" value="yes"> Yes
            </label>
          </div>
        </div>

        <div *ngIf="knowsStandards">
          <div class="form-group">
            <label>Please provide more details</label>
            <textarea class="form-control" rows="4"
                      formControlName="standards_details"
                      placeholder="Describe the standards or areas of standardisation that are relevant to your project…"></textarea>
          </div>

          <div formArrayName="standard_reference">
            <div *ngFor="let refGroup of standardReference.controls; let i = index"
                 [formGroupName]="i" class="panel panel-default entry-panel">
              <div class="panel-heading">
                <strong>Standards body and technical committee</strong>
              </div>
              <div class="panel-body">
                <div class="form-group">
                  <label>Select the standards body you'd like to get in touch with or discover more about</label>
                  <select class="form-control" formControlName="sdo">
                    <option value="">— Select —</option>
                    <optgroup *ngFor="let grp of SDO_OPTIONS" [label]="grp.group">
                      <option *ngFor="let opt of grp.options" [value]="opt">{{ opt }}</option>
                    </optgroup>
                  </select>
                </div>
                <div *ngIf="isOtherSdo(refGroup)" class="form-group">
                  <label>Please specify the standards body</label>
                  <input type="text" class="form-control" formControlName="other_options">
                </div>
                <div class="row">
                  <div class="col-md-5">
                    <div class="form-group">
                      <label>Technical committee, sub-committee or working group code</label>
                      <input type="text" class="form-control" formControlName="tc_number"
                             placeholder="e.g. ISO/IEC JTC 1/SC 27">
                    </div>
                  </div>
                  <div class="col-md-7">
                    <div class="form-group">
                      <label>Full name of the committee</label>
                      <input type="text" class="form-control" formControlName="tc_title"
                             placeholder="e.g. Information security, cybersecurity and privacy protection">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Q6. AVAILABILITY, LANGUAGES AND PREFERRED SERVICE ──────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q6. Availability, languages and preferred service</h4>
        <p class="text-muted">Please tell us the language(s) in which you would like to receive support and your time zone, so that a suitable expert can be scheduled.</p>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group" id="field-preferred_languages">
              <label>In which language(s) would you like to receive support? English is always available. <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('preferred_languages')?.invalid"
                     formControlName="preferred_languages"
                     placeholder="e.g. English, French, German">
              <div *ngIf="formSubmitted && form.get('preferred_languages')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group" id="field-time_zone">
              <label>Time zone <span class="req">*</span></label>
              <input type="text" class="form-control"
                     [class.input-error]="formSubmitted && form.get('time_zone')?.invalid"
                     formControlName="time_zone"
                     placeholder="e.g. CET/CEST">
              <div *ngIf="formSubmitted && form.get('time_zone')?.invalid" class="field-error">
                This field is required.
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Scheduling constraints</label>
          <textarea class="form-control" rows="3"
                    formControlName="scheduling_constraints"
                    placeholder="Optional. Mention any constraints that could affect scheduling, for example holidays or other commitments."></textarea>
        </div>
      </div>

      <!-- ── Q7. CONSENT AND DECLARATIONS ──────────────────────────────────── -->
      <div class="form-section">
        <h4 class="form-section-title">Q7. Consent and declarations</h4>
        <p class="text-muted">Please review and confirm the following before submitting your application.</p>

        <div class="form-group" id="field-website_publication">
          <label>Do you authorise us to publish a public profile of your project in the Project Hub section of the HSbooster.eu website? <span class="req">*</span></label>
          <p class="text-muted small">
            We'd like to create public profiles on the projects which apply for HSbooster.eu services.
            This will give your project visibility as being active in the standardisation area.
          </p>
          <div>
            <label class="radio-inline">
              <input type="radio" formControlName="website_publication" [value]="true"> Yes
            </label>
            <label class="radio-inline">
              <input type="radio" formControlName="website_publication" [value]="false"> No
            </label>
          </div>
          <div *ngIf="formSubmitted && form.get('website_publication')?.invalid" class="field-error">
            Please select an option.
          </div>
        </div>

        <div class="form-group" id="field-data_consent">
          <label class="checkbox-inline"
                 [class.input-error]="formSubmitted && form.get('data_consent')?.invalid">
            <input type="checkbox" formControlName="data_consent">
            I consent to the processing of the data provided in this application for the assessment, management and delivery of HSbooster.eu II services. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('data_consent')?.invalid" class="field-error">
            This declaration is required.
          </div>
        </div>

        <div class="form-group" id="field-accuracy_declaration">
          <label class="checkbox-inline"
                 [class.input-error]="formSubmitted && form.get('accuracy_declaration')?.invalid">
            <input type="checkbox" formControlName="accuracy_declaration">
            I confirm that the information provided in this application is accurate and can be verified. <span class="req">*</span>
          </label>
          <div *ngIf="formSubmitted && form.get('accuracy_declaration')?.invalid" class="field-error">
            This declaration is required.
          </div>
        </div>
      </div>

      <button type="submit" class="btn btn-primary btn-fill" [disabled]="submitting">
        <span *ngIf="submitting"><i class="fa fa-spinner fa-spin"></i> Submitting...</span>
        <span *ngIf="!submitting">Submit Application</span>
      </button>
      <button type="button" class="btn btn-default" style="margin-left: 10px;" [disabled]="savingDraft" (click)="saveDraft()">
        <span *ngIf="savingDraft"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
        <span *ngIf="!savingDraft"><i class="fa fa-floppy-o"></i> Save as Draft</span>
      </button>
      <span *ngIf="draftSaved" class="text-success" style="margin-left: 10px; font-size: 13px;">
        <i class="fa fa-check"></i> Draft saved
      </span>
      <a routerLink="/my-applications" class="form-cancel-link">Cancel</a>

    </form>
  </div>
</div>
